/*
 * Purpose: Builder layout — resizable config panel with auto open/close on node select
 * Last modified: 2026-06-26 | Agent
 * Completeness: 92
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { Canvas } from '@/components/Canvas';
import { WorkflowPanel } from '@/components/WorkflowPanel';
import { ExecutionResults } from '@/components/ExecutionResults';
import { FlowControlBar } from '@/components/FlowControlBar';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { useIsMobile } from '@/hooks/use-mobile';
import { useWorkflowStore } from '@/stores/workflowStore';
import type { ExecutionContext } from '@/services/executor';
import { toast } from 'sonner';
import { WorkflowValidator } from '@/services/validation';

interface BuilderWorkspaceProps {
  /** When true, register flow-scoped keyboard shortcuts (save / run). */
  shortcutsActive?: boolean;
}

const CONFIG_PANEL_SIZE = 28;
const RESULTS_PANEL_SIZE = 22;

export function BuilderWorkspace({ shortcutsActive = true }: BuilderWorkspaceProps) {
  const isMobile = useIsMobile();
  const { selectedNode, selectNode, currentWorkflow, saveWorkflow } = useWorkflowStore();
  const configPanelRef = useRef<ImperativePanelHandle>(null);
  const [isExecuting, setIsExecuting] = useState(false);
  const [executionResults, setExecutionResults] = useState<ExecutionContext[]>([]);
  const hasResults = executionResults.length > 0;
  const isConfigOpen = Boolean(selectedNode);

  const abortRef = useRef<AbortController | null>(null);

  const handleExecute = useCallback(async () => {
    if (!currentWorkflow) {
      toast.error('No flow selected');
      return;
    }

    const errors = WorkflowValidator.validate(currentWorkflow).filter((e) => e.type === 'error');
    if (errors.length > 0) {
      toast.error('Fix check issues before starting');
      return;
    }

    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setIsExecuting(true);
    setExecutionResults([]);
    try {
      const results = await useWorkflowStore.getState().executeWorkflow(controller.signal);
      if (!controller.signal.aborted) {
        setExecutionResults(results);
        toast.success('Flow started and completed');
      }
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        toast.message('Flow stopped');
        return;
      }
      const msg = error instanceof Error ? error.message : 'Execution failed';
      toast.error(msg);
    } finally {
      if (abortRef.current === controller) {
        abortRef.current = null;
      }
      setIsExecuting(false);
    }
  }, [currentWorkflow]);

  const handleStop = useCallback(() => {
    abortRef.current?.abort();
    setIsExecuting(false);
    toast.message('Stopping flow…');
  }, []);

  useEffect(() => {
    if (!shortcutsActive) return;

    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 's') {
        e.preventDefault();
        saveWorkflow();
        toast.success('Flow saved');
      }
      if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
        e.preventDefault();
        void handleExecute();
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [handleExecute, saveWorkflow, shortcutsActive]);

  useEffect(() => {
    if (isMobile) return;
    const panel = configPanelRef.current;
    if (!panel) return;

    if (selectedNode) {
      panel.expand();
      panel.resize(CONFIG_PANEL_SIZE);
    } else {
      panel.collapse();
    }
  }, [selectedNode, isMobile]);

  const handleCloseConfig = () => selectNode(null);

  if (isMobile) {
    return (
      <div className="h-full min-h-0 w-full flex flex-col gap-0 rounded-lg border border-border overflow-hidden">
        <FlowControlBar
          isExecuting={isExecuting}
          onExecute={handleExecute}
          onStop={handleStop}
        />
        <div className="flex-1 min-h-0 overflow-hidden p-2 pt-0">
          <Canvas />
        </div>
        <Sheet open={isConfigOpen} onOpenChange={(open) => !open && handleCloseConfig()}>
          <SheetContent side="right" className="w-full sm:max-w-md p-0 flex flex-col">
            <WorkflowPanel onClose={handleCloseConfig} />
          </SheetContent>
        </Sheet>
        {hasResults && (
          <div className="mt-3 rounded-lg border border-border bg-card overflow-hidden max-h-48">
            <div className="p-3 border-b border-border">
              <h3 className="font-semibold text-sm text-foreground">Results</h3>
            </div>
            <div className="p-3 overflow-auto">
              <ExecutionResults results={executionResults} />
            </div>
          </div>
        )}
      </div>
    );
  }

  const canvasDefaultSize = hasResults ? 100 - CONFIG_PANEL_SIZE - RESULTS_PANEL_SIZE : 100 - CONFIG_PANEL_SIZE;

  return (
    <div className="h-full min-h-0 w-full flex flex-col rounded-lg border border-border overflow-hidden">
      <FlowControlBar
        isExecuting={isExecuting}
        onExecute={handleExecute}
        onStop={handleStop}
      />
      <ResizablePanelGroup
        direction="horizontal"
        className="flex-1 min-h-0 w-full"
      >
      <ResizablePanel defaultSize={canvasDefaultSize} minSize={35} order={1}>
        <div className="h-full min-h-0">
          <Canvas />
        </div>
      </ResizablePanel>

      <ResizableHandle withHandle className="bg-border" />

      <ResizablePanel
        ref={configPanelRef}
        order={2}
        collapsible
        collapsedSize={0}
        defaultSize={0}
        minSize={18}
        maxSize={42}
        className="bg-card"
      >
        {isConfigOpen ? (
          <WorkflowPanel onClose={handleCloseConfig} />
        ) : (
          <div className="h-full w-full" aria-hidden />
        )}
      </ResizablePanel>

      {hasResults && (
        <>
          <ResizableHandle withHandle className="bg-border" />
          <ResizablePanel
            order={3}
            defaultSize={RESULTS_PANEL_SIZE}
            minSize={15}
            maxSize={35}
            className="bg-card border-l border-border"
          >
            <div className="h-full flex flex-col overflow-hidden">
              <div className="p-4 border-b border-border shrink-0">
                <h3 className="font-semibold text-foreground">Results</h3>
              </div>
              <div className="flex-1 p-4 overflow-auto min-h-0">
                <ExecutionResults results={executionResults} />
              </div>
            </div>
          </ResizablePanel>
        </>
      )}
      </ResizablePanelGroup>
    </div>
  );
}
